/**
 * Native Surfstr reviews carousel (testimonials + video UGC).
 * Data: theme/assets/surfstr-reviews-data.json
 */
const STAR = "★";

const I18N = (() => {
  const node = document.getElementById("SurfstrReviewsI18n");
  if (!node) return {};
  try {
    return JSON.parse(node.textContent || "{}");
  } catch {
    return {};
  }
})();

const interpolate = (template, vars) => {
  if (!template) return "";
  let out = template;
  for (const [key, value] of Object.entries(vars)) {
    out = out.replaceAll(`{{ ${key} }}`, String(value));
  }
  return out;
};

const qs = (sel, root) => (root || document).querySelector(sel);

const qsa = (sel, root) => Array.from((root || document).querySelectorAll(sel));

const stars = (n) => {
  const count = Math.max(0, Math.min(5, Number(n) || 5));
  return STAR.repeat(count);
};

const formatDuration = (sec) => {
  if (!sec) return "";
  const s = Math.round(sec);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}:${String(r).padStart(2, "0")}` : `0:${String(r).padStart(2, "0")}`;
};

const escapeHtml = (str) =>
  String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const escapeAttr = (str) => escapeHtml(str).replace(/'/g, "&#39;");

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

class ReviewsCarousel {
  #index = 0;
  #progressFill = null;
  #progressBar = null;

  constructor(track, prev, next, dotsRoot, slideCount, progressFill = null) {
    this.track = track;
    this.prev = prev;
    this.next = next;
    this.dotsRoot = dotsRoot;
    this.slideCount = slideCount;
    this.#progressFill = progressFill;
    this.#progressBar = progressFill?.closest("[role='progressbar']") ?? null;
    this.#bind();
    this.#updateProgress();
    this.#updateArrows();
  }

  #bind() {
    this.prev?.addEventListener("click", () => this.#go(this.#index - 1));
    this.next?.addEventListener("click", () => this.#go(this.#index + 1));

    for (const dot of qsa("[data-dot]", this.dotsRoot)) {
      dot.addEventListener("click", () => this.#go(Number(dot.dataset.dot)));
    }

    this.track.addEventListener("keydown", (e) => this.#onKeydown(e));

    let scrollTimer;
    let progressRaf;
    this.track.addEventListener(
      "scroll",
      () => {
        if (progressRaf) cancelAnimationFrame(progressRaf);
        progressRaf = requestAnimationFrame(() => this.#updateProgress());

        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => this.#syncFromScroll(), 80);
      },
      { passive: true }
    );

    window.addEventListener("resize", () => {
      this.#updateProgress();
      this.#updateArrows();
    });
  }

  #scrollBehavior() {
    return prefersReducedMotion() ? "auto" : "smooth";
  }

  #onKeydown(e) {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      this.#go(this.#index - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      this.#go(this.#index + 1);
    }
  }

  #go(i) {
    if (!this.slideCount) return;

    const nextIndex = (i + this.slideCount) % this.slideCount;
    const slide = this.track.children[nextIndex];
    if (!slide) return;

    this.#index = nextIndex;
    slide.scrollIntoView({
      behavior: this.#scrollBehavior(),
      block: "nearest",
      inline: "center",
    });
    this.#updateDots();
    this.#updateProgress();
    this.#updateArrows();
  }

  #syncFromScroll() {
    const center = this.track.scrollLeft + this.track.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;

    for (const [i, child] of Array.from(this.track.children).entries()) {
      const childCenter = child.offsetLeft + child.offsetWidth / 2;
      const dist = Math.abs(childCenter - center);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }

    if (best !== this.#index) {
      this.#index = best;
      this.#updateDots();
      this.#updateArrows();
    }
  }

  #updateDots() {
    if (!this.dotsRoot) return;
    for (const [di, dot] of qsa("[data-dot]", this.dotsRoot).entries()) {
      dot.setAttribute("aria-selected", di === this.#index ? "true" : "false");
    }
  }

  #updateProgress() {
    if (!this.#progressFill) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    let pct = 100;
    if (maxScroll > 1) {
      pct = Math.min(100, Math.max(0, (this.track.scrollLeft / maxScroll) * 100));
    } else if (this.slideCount > 1) {
      pct = (this.#index / (this.slideCount - 1)) * 100;
    }

    // Thumb-style scrollbar: the thumb is as wide as the visible share of the track and slides with it.
    const frac = this.track.scrollWidth > 0 ? Math.min(1, this.track.clientWidth / this.track.scrollWidth) : 1;
    this.#progressFill.style.width = `${Math.max(8, frac * 100)}%`;
    this.#progressFill.style.left = `${(pct / 100) * (100 - Math.max(8, frac * 100))}%`;
    this.#progressBar?.setAttribute("aria-valuenow", String(Math.round(pct)));
    const counter = this.#progressBar?.parentElement?.querySelector("[data-counter]");
    if (counter && this.slideCount) {
      const first = this.track.firstElementChild;
      const step = first ? first.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap || "0") : 0;
      const visible = step > 0 ? Math.max(1, Math.floor(this.track.clientWidth / step)) : 1;
      const start = step > 0 ? Math.min(this.slideCount - visible, Math.round(this.track.scrollLeft / step)) : 0;
      const end = Math.min(this.slideCount, Math.max(start, 0) + visible);
      const from = Math.max(1, start + 1);
      counter.textContent = from === end ? `${from} / ${this.slideCount}` : `${from}–${end} / ${this.slideCount}`;
    }
  }

  #updateArrows() {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const atStart = this.track.scrollLeft <= 1;
    const atEnd = maxScroll <= 1 || this.track.scrollLeft >= maxScroll - 1;

    this.prev?.toggleAttribute("disabled", atStart);
    this.prev?.setAttribute("aria-disabled", atStart ? "true" : "false");
    this.next?.toggleAttribute("disabled", atEnd);
    this.next?.setAttribute("aria-disabled", atEnd ? "true" : "false");
  }
}

const buildTestimonials = (root, items) => {
  const track = qs("[data-track]", root);
  const prev = qs("[data-prev]", root);
  const next = qs("[data-next]", root);
  const dots = qs("[data-dots]", root);
  if (!track || !items.length) return;

  track.innerHTML = items
    .map((item, i) => {
      const slideLabel = interpolate(I18N.slideOf, {
        index: i + 1,
        total: items.length,
      });
      const starLabel = interpolate(I18N.starRating, { count: item.stars });
      return `
      <article class="surfstr-reviews__slide" role="listitem" aria-roledescription="slide" aria-label="${escapeAttr(slideLabel)}">
        <blockquote class="surfstr-reviews__quote">
          <span class="surfstr-reviews__mark" aria-hidden="true">"</span>
          <p class="surfstr-reviews__stars" aria-label="${escapeAttr(starLabel)}">${stars(item.stars)}</p>
          <p class="surfstr-reviews__text">${escapeHtml(item.text)}</p>
          <footer><cite class="surfstr-reviews__author">${escapeHtml(item.name)}</cite></footer>
        </blockquote>
      </article>`;
    })
    .join("");

  if (dots) {
    dots.innerHTML = items
      .map((_, i) => {
        const dotLabel = interpolate(I18N.reviewDot, { index: i + 1 });
        return `<button type="button" class="surfstr-reviews__dot" data-dot="${i}" aria-label="${escapeAttr(dotLabel)}" aria-selected="${i === 0 ? "true" : "false"}"></button>`;
      })
      .join("");
  }

  new ReviewsCarousel(track, prev, next, dots, items.length);
};

const buildVideos = (root, items) => {
  const track = qs("[data-track]", root);
  const prev = qs("[data-prev]", root);
  const next = qs("[data-next]", root);
  // Dialog is retained only for photo lightbox; videos play inline in the card.
  const dialog = qs("[data-video-dialog]", root);
  const dialogVideo = qs("[data-dialog-video]", root);
  const dialogClose = qs("[data-dialog-close]", root);
  if (!track || !items.length) return;

  track.innerHTML = items
    .map((item) => {
      const isVideo = item.type === "video";
      const duration = isVideo ? formatDuration(item.duration) : "";
      const videoLabel = interpolate(I18N.videoFrom, { name: item.name });
      // role=button (not <button>) so an inline <video controls> can live inside it.
      return `
      <article class="surfstr-video-reviews__card" role="listitem">
        <div class="surfstr-video-reviews__trigger" data-video-trigger role="button" tabindex="0"
          data-streaming="${escapeAttr(item.streaming || "")}"
          data-poster="${escapeAttr(item.poster || "")}"
          data-alt="${escapeAttr(item.alt || "")}"
          ${isVideo ? "" : 'data-photo="true"'}
          aria-label="${escapeAttr(videoLabel)}">
          <img class="surfstr-video-reviews__poster" src="${escapeAttr(item.poster)}" alt="${escapeAttr(item.alt || item.name)}" loading="lazy" width="360" height="640" />
          ${isVideo ? `<span class="surfstr-video-reviews__play" aria-hidden="true"></span>` : ""}
          ${duration ? `<span class="surfstr-video-reviews__duration">${duration}</span>` : ""}
        </div>
        <p class="surfstr-video-reviews__meta">
          <span class="surfstr-video-reviews__stars" aria-hidden="true">${stars(item.rating)}</span>
          <span class="surfstr-video-reviews__name">${escapeHtml(item.name)}</span>
        </p>
      </article>`;
    })
    .join("");

  // A poster that fails to load must not leave a broken image: the card falls
  // back to the brand gradient (see .has-no-poster in surfstr-mobile.css).
  for (const img of qsa(".surfstr-video-reviews__poster", track)) {
    img.addEventListener("error", () => {
      img.closest("[data-video-trigger]")?.classList.add("has-no-poster");
    });
  }

  // Only one inline video plays at a time.
  let current = null;
  const stopCurrent = () => {
    if (!current) return;
    current.observer?.disconnect();
    if (typeof current.video.pause === "function") current.video.pause();
    current.video.remove();
    current.trigger.classList.remove("is-playing");
    current.trigger.setAttribute("aria-pressed", "false");
    current = null;
  };

  /**
   * The customer videos live on Cloudflare Stream. Browsers with native HLS
   * (Safari, iOS) get an inline <video> on the HLS manifest; every other
   * browser gets the Stream player iframe for the same video, so no
   * third-party transcode is needed.
   */
  const streamPlayerUrl = (manifestUrl, poster) => {
    try {
      const url = new URL(manifestUrl);
      const uid = url.pathname.split("/").filter(Boolean)[0];
      if (!url.hostname.endsWith(".cloudflarestream.com") || !uid) return "";
      const params = new URLSearchParams({ autoplay: "true", preload: "metadata" });
      if (poster) params.set("poster", poster);
      return `${url.origin}/${uid}/iframe?${params.toString()}`;
    } catch {
      return "";
    }
  };

  const playInline = (trigger) => {
    if (trigger.classList.contains("is-playing")) return;
    stopCurrent();

    const stream = trigger.dataset.streaming;
    if (!stream) return;
    const poster = trigger.dataset.poster || "";
    const probe = document.createElement("video");
    const nativeHls = probe.canPlayType("application/vnd.apple.mpegurl") !== "";
    const playerUrl = nativeHls ? "" : streamPlayerUrl(stream, poster);

    let video;
    if (playerUrl) {
      video = document.createElement("iframe");
      video.className = "surfstr-video-reviews__inline";
      video.src = playerUrl;
      video.title = trigger.getAttribute("aria-label") || "";
      video.allow = "autoplay; fullscreen; picture-in-picture";
      video.allowFullscreen = true;
    } else {
      video = probe;
      video.className = "surfstr-video-reviews__inline";
      video.controls = true;
      video.autoplay = true;
      video.playsInline = true;
      video.setAttribute("playsinline", "");
      video.poster = poster;
      video.src = stream;
      video.addEventListener("ended", stopCurrent);
    }

    trigger.appendChild(video);
    trigger.classList.add("is-playing");
    trigger.setAttribute("aria-pressed", "true");

    // Stop playback when the card is scrolled mostly out of view.
    let observer = null;
    if ("IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) stopCurrent();
          }
        },
        { threshold: 0.4 }
      );
      observer.observe(trigger);
    }

    current = { trigger, video, observer };
    if (typeof video.play === "function") video.play().catch(() => {});
    video.focus({ preventScroll: true });
  };

  const openPhoto = (trigger) => {
    if (!dialog) return;
    dialogVideo?.removeAttribute("src");
    dialogVideo?.classList.add("hidden");
    let img = qs("[data-dialog-photo]", dialog);
    if (!img) {
      img = document.createElement("img");
      img.className = "surfstr-video-reviews__dialog-photo";
      img.dataset.dialogPhoto = "";
      dialog.appendChild(img);
    }
    img.src = trigger.dataset.poster || "";
    img.alt = trigger.dataset.alt || "";
    img.hidden = false;
    dialog.showModal();
  };

  const activate = (trigger) => {
    if (trigger.dataset.photo === "true") openPhoto(trigger);
    else playInline(trigger);
  };

  for (const trigger of qsa("[data-video-trigger]", track)) {
    trigger.addEventListener("click", () => activate(trigger));
    trigger.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
        e.preventDefault();
        activate(trigger);
      }
    });
  }

  dialogClose?.addEventListener("click", () => dialog?.close());
  dialog?.addEventListener("close", () => {
    const img = qs("[data-dialog-photo]", dialog);
    if (img) {
      img.removeAttribute("src");
      img.hidden = true;
    }
  });

  const progressFill = qs("[data-progress-fill]", root);
  new ReviewsCarousel(track, prev, next, null, items.length, progressFill);
};

const loadData = async (src) => {
  const res = await fetch(src, { credentials: "same-origin" });
  if (!res.ok) throw new Error(`Reviews data ${res.status}`);
  return res.json();
};

const boot = async () => {
  if (window.__surfstrReviewsBooted) return;
  const roots = qsa("[data-surfstr-reviews-root]");
  if (!roots.length) return;
  window.__surfstrReviewsBooted = true;

  const src = roots[0].dataset.src;
  if (!src) return;

  let data;
  try {
    data = await loadData(src);
  } catch (e) {
    console.warn("Surfstr reviews:", e);
    return;
  }

  for (const root of roots) {
    const mode = root.dataset.mode;
    if (mode === "testimonials") buildTestimonials(root, data.testimonials || []);
    if (mode === "videos") buildVideos(root, data.videos || []);
  }

  const summary = qs("[data-reviews-summary]");
  if (summary && data.averageRating) {
    summary.textContent = interpolate(I18N.summary, {
      rating: data.averageRating.toFixed(1),
      count: data.reviewCount,
    });
    summary.hidden = false;
  }
};

/** Replace stale page_cache hero markup with a fresh Section Rendering API response. */
const refreshHeroIfStale = async () => {
  const root = document.querySelector(".surfstr-section-hero");
  if (!root) return;
  const heroMarker = root.querySelector("[data-surfstr-hero]");
  if (heroMarker?.getAttribute("data-surfstr-hero") === "pour-asset") return;
  const img = root.querySelector(".surfstr-lovable-hero__img");
  if (
    img?.src.includes("surfstr-hero-pour") ||
    img?.src.includes("surfstr-hero-product")
  ) {
    return;
  }
  const sectionKey = root.id.replace(/^shopify-section-/, "");
  if (!sectionKey) return;
  try {
    const sep = window.location.search ? "&" : "?";
    const url = `${window.location.pathname}${window.location.search}${sep}sections=${encodeURIComponent(sectionKey)}`;
    const res = await fetch(url, { credentials: "same-origin" });
    const data = await res.json();
    const html = data[sectionKey];
    if (!html) return;
    const holder = document.createElement("div");
    holder.innerHTML = html;
    const fresh = holder.querySelector(".shopify-section");
    if (fresh) root.replaceWith(fresh);
  } catch (_) {
    /* ignore */
  }
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    refreshHeroIfStale();
    boot();
  });
} else {
  refreshHeroIfStale();
  boot();
}
